// Streamlined Quick Local Transport Form (client requirement, 2026-10-13):
// Only ONE single quick form — no confusing switches or full form duplicates.
//
// 1. Operational details (filled by Dispatch team & Admins):
//    - Transporter / Courier name (with quick chips)
//    - Vehicle number (with quick chips)
//    - Driver / Transporter mobile number
//    - LR / Tracking / Docket no.
//    - To Pay / Paid
//    - Payment mode
//    - Note
//
// 2. Financial Amount (entered/edited by Super Admin, Admin, Department Admin ONLY):
//    - canSetTransportAmount(user): full edit access to enter/update amount (₹).
//    - Non-admin dispatch operators see a clear note that amount is entered by Admin.

import { useState } from 'react';
import { Truck, X, Zap, DollarSign, ShieldAlert } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useUser } from '@/shared/hooks/useAuth';
import { canSetTransportAmount } from '@/shared/utils/dispatchAccess';

export interface LocalTransport {
  used: string;
  name: string;
  mobile: string;
  amount: string;
  vehicleNo: string;
  paymentMode: string;
  payment: string;
  trackingNo: string;
  note: string;
  /** Read-only prefill from the hand-off; never sent back (see handleSubmit). */
  freightAmount?: string;
  freightPayment?: string;
  tpName: string;
  tpMobile: string;
  tpVehicleNo: string;
  tpPaymentMode: string;
  tpTrackingNo: string;
  tpNote: string;
  thirdParty?: { name: string; mobile: string; vehicleNo: string; paymentMode: string; trackingNo: string; note: string };
  amountEntered?: boolean;
  freightAmountEntered?: boolean;
  divideAcrossVehicle?: boolean;
  /** The order's trip day (YYYY-MM-DD, IST) — prefills the vehicle-split date. */
  tripDate?: string;
  /** The day whose orders on this vehicle share the cost (sent with divideAcrossVehicle). */
  shareDate?: string;
}

const istDay = (d: unknown) => {
  if (!d) return '';
  const t = new Date(String(d));
  return Number.isNaN(t.getTime()) ? '' : t.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

/** After a save: say how a vehicle's cost was split, so nobody has to guess. */
export function announceVehicleShare(data: unknown) {
  const shared = (data as { shared?: { date?: string; orders?: number } } | undefined)?.shared;
  if (!shared?.date) return;
  const day = new Date(`${shared.date}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  toast(shared.orders && shared.orders > 1
    ? `Cost split equally across ${shared.orders} orders on this vehicle on ${day}.`
    : `No other order on this vehicle on ${day} — the full cost stays on this order.`, { icon: '🚚', duration: 6000 });
}

export const EMPTY_LOCAL_TRANSPORT: LocalTransport = {
  used: '', name: '', mobile: '', amount: '', vehicleNo: '', paymentMode: '', payment: '', trackingNo: '', note: '',
  tpName: '', tpMobile: '', tpVehicleNo: '', tpPaymentMode: '', tpTrackingNo: '', tpNote: '',
};

export const LOCAL_TRANSPORT_PAYMENT_MODES = [
  'To pay (on delivery)',
  'Cash',
  'UPI',
  'Bank transfer',
  'Credit (pay later)',
];

// Checklist Item 56: "Local Transport - Dropdown with Prashant Bhate, Suresh Solanki, Paid Porter, To Pay Porter, Self, Add New"
export const LOCAL_TRANSPORT_DROPDOWN_OPTIONS = [
  { value: '', label: '-- Select Local Transporter --' },
  { value: 'Prashant Bhate', label: 'Prashant Bhate' },
  { value: 'Suresh Solanki', label: 'Suresh Solanki' },
  { value: 'Paid Porter', label: 'Paid Porter' },
  { value: 'To Pay Porter', label: 'To Pay Porter' },
  { value: 'Self', label: 'Self' },
  { value: '__add_new__', label: '+ Add New' },
];

const QUICK_TRANSPORTERS = [
  'Prashant Bhate',
  'Suresh Solanki',
  'Paid Porter',
  'To Pay Porter',
  'Self',
];

const QUICK_VEHICLES = ['Own Tempo', 'Tempo', 'Hand Delivery', 'Customer Pickup'];

export function localTransportToForm(v?: Record<string, unknown> | null, handoff?: Record<string, unknown> | null): LocalTransport {
  const s = (x: unknown) => (x === null || x === undefined ? '' : String(x));
  const freight = (handoff?.freight || {}) as Record<string, unknown>;
  const tp = (handoff?.thirdPartyTransport || {}) as Record<string, unknown>;
  const usedFlag = handoff?.localTransportUsed;
  const ltAmount = s(v?.amount) || s(freight.amount);
  const ltPayment = s(v?.payment) || s(freight.payment) || (ltAmount ? 'paid' : 'to_pay');

  return {
    used: usedFlag === true ? 'yes' : usedFlag === false ? 'no' : (s(v?.name) ? 'yes' : ''),
    name: s(v?.name) || s(tp.name) || s(handoff?.courierName),
    mobile: s(v?.mobile ?? v?.driverPhone) || s(tp.mobile),
    amount: ltAmount,
    vehicleNo: s(v?.vehicleNo) || s(tp.vehicleNo),
    paymentMode: s(v?.paymentMode) || s(tp.paymentMode) || 'To pay (on delivery)',
    payment: ltPayment,
    trackingNo: s(v?.trackingNo) || s(tp.trackingNo),
    note: s(v?.note) || s(tp.note),
    amountEntered: v?.amountEntered === true || ltAmount !== '',
    freightAmount: s(freight.amount) || ltAmount,
    freightPayment: s(freight.payment) || ltPayment,
    freightAmountEntered: freight?.amountEntered === true || s(freight.amount) !== '',
    tpName: s(tp.name) || s(handoff?.courierName),
    tpMobile: s(tp.mobile),
    tpVehicleNo: s(tp.vehicleNo),
    tpPaymentMode: s(tp.paymentMode),
    tpTrackingNo: s(tp.trackingNo),
    tpNote: s(tp.note),
    // Dispatched date, else the dispatch due date, else the hand-off date —
    // the same day the server splits a shared vehicle's cost on.
    tripDate: istDay(handoff?.dispatchedAt || handoff?.requiredDispatchDate || handoff?.handedOffAt),
  };
}

export interface LocalTransportModalProps {
  orderNumber?: string;
  sentVia?: string;
  thirdParty?: boolean;
  localOnly?: boolean;
  busy?: boolean;
  initial?: LocalTransport;
  mode?: 'dispatch' | 'edit';
  twoLegs?: boolean;
  bulkRecords?: Array<{ _id: string; orderNumber: string; customerName?: string }>;
  onCancel: () => void;
  onConfirm: (lt: LocalTransport, bulkIds?: string[]) => void;
}

export function LocalTransportModal({
  orderNumber,
  sentVia,
  thirdParty: _thirdParty = false,
  localOnly = false,
  busy,
  initial,
  bulkRecords,
  onCancel,
  onConfirm,
}: LocalTransportModalProps) {
  const user = useUser();
  const canSetAmount = canSetTransportAmount(user);
  const isBulk = Array.isArray(bulkRecords) && bulkRecords.length > 1;

  const [name, setName] = useState(() => initial?.name || initial?.tpName || sentVia || '');
  const [vehicleNo, setVehicleNo] = useState(() => initial?.vehicleNo || initial?.tpVehicleNo || '');
  const [trackingNo, setTrackingNo] = useState(() => initial?.trackingNo || initial?.tpTrackingNo || '');
  const [mobile, setMobile] = useState(() => initial?.mobile || initial?.tpMobile || '');
  const [payment, setPayment] = useState(() => initial?.payment || (initial?.amount ? 'paid' : 'to_pay'));
  const [paymentMode, setPaymentMode] = useState(() => initial?.paymentMode || 'To pay (on delivery)');
  const [amount, setAmount] = useState(() => initial?.amount || initial?.freightAmount || '');
  const [note, setNote] = useState(() => initial?.note || initial?.tpNote || '');
  const [divideAcrossVehicle, setDivideAcrossVehicle] = useState(true);
  const [shareDate, setShareDate] = useState(() => initial?.tripDate || '');

  const applyTransporterPreset = (val: string) => {
    if (!val || val === '__add_new__') {
      if (val === '__add_new__') setName('');
      return;
    }
    if (val === 'Paid Porter') {
      setName('Paid Porter');
      setPayment('paid');
      setPaymentMode('Cash');
    } else if (val === 'To Pay Porter') {
      setName('To Pay Porter');
      setPayment('to_pay');
      setPaymentMode('To pay (on delivery)');
    } else if (val === 'Self') {
      setName('Self');
      if (!vehicleNo) setVehicleNo('Self Pickup');
      setPayment('self');
    } else {
      setName(val);
    }
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const finalName = name.trim() || 'Local Transport';
    const finalVehicle = vehicleNo.trim() ? vehicleNo.trim().toUpperCase() : 'LOCAL';
    const finalMobile = mobile.trim() || '9999999999';
    const finalAmount = amount.trim();

    const common = {
      name: finalName,
      mobile: finalMobile,
      vehicleNo: finalVehicle,
      paymentMode,
      payment: payment || (finalAmount ? 'paid' : 'to_pay'),
      amount: finalAmount,
      trackingNo: trackingNo.trim(),
      note: note.trim(),
      divideAcrossVehicle: canSetAmount ? divideAcrossVehicle : false,
      ...(canSetAmount && divideAcrossVehicle && shareDate ? { shareDate } : {}),
    };

    if (isBulk && bulkRecords) {
      onConfirm({
        ...EMPTY_LOCAL_TRANSPORT,
        ...common,
        freightAmount: finalAmount,
        freightPayment: common.payment,
      }, bulkRecords.map((r) => r._id));
      return;
    }

    // No freight fields: this form records the LOCAL leg. Mirroring its amount
    // and To Pay / Paid into the third-party freight overwrote the term Sales
    // chose at hand-off (ACPL "To Pay" turned "Paid" when the Porter was paid)
    // and showed the local cost a second time as freight, doubling the
    // register's total. Freight is optional and entered on its own.
    const payload: LocalTransport = {
      ...EMPTY_LOCAL_TRANSPORT,
      used: 'yes',
      ...common,
      thirdParty: localOnly ? undefined : {
        name: finalName,
        mobile: finalMobile,
        vehicleNo: finalVehicle,
        paymentMode,
        trackingNo: trackingNo.trim(),
        note: note.trim(),
      },
    };

    onConfirm(payload);
  };

  const fieldClass = 'w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2 text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition';
  const labelClass = 'mb-1 block text-xs font-bold text-slate-700';

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs" onClick={onCancel}>
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 p-5 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
              <Truck className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-extrabold text-slate-900">
                  {isBulk ? `Quick Transport (${bulkRecords.length} Orders)` : `Transport Details — ${orderNumber || 'Order'}`}
                </h3>
                <span className="text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-full">
                  Quick Form
                </span>
              </div>
              <p className="text-[11px] text-slate-500">
                Single unified form for local &amp; courier dispatch details
              </p>
            </div>
          </div>
          <button type="button" onClick={onCancel} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          {/* Quick Transporter Dropdown & Preset Buttons (Item 56) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelClass}>Local Transporter (Dropdown / Preset)</label>
              <span className="text-[10px] text-slate-400 font-medium">Select preset or enter custom</span>
            </div>
            <select
              className={`${fieldClass} mb-2 bg-white font-semibold text-slate-800`}
              value={LOCAL_TRANSPORT_DROPDOWN_OPTIONS.some((opt) => opt.value === name) ? name : (name ? '__add_new__' : '')}
              onChange={(e) => applyTransporterPreset(e.target.value)}
            >
              {LOCAL_TRANSPORT_DROPDOWN_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {QUICK_TRANSPORTERS.map((t) => {
                const isActive = name === t || (t === 'Self' && name.toLowerCase() === 'self');
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => applyTransporterPreset(t)}
                    className={`text-[11px] font-medium px-2.5 py-1 rounded-lg border transition cursor-pointer ${
                      isActive
                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-2xs font-semibold'
                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
            <input
              className={fieldClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Prashant Bhate, Suresh Solanki, Porter, Self, or Custom..."
              required
            />
          </div>

          {/* Vehicle Number & Quick Buttons */}
          <div>
            <label className={labelClass}>Vehicle Number</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {QUICK_VEHICLES.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVehicleNo(v)}
                  className={`text-[11px] font-medium px-2 py-0.5 rounded-lg border transition ${
                    vehicleNo === v
                      ? 'bg-success-text text-on-primary border-success/40 font-semibold'
                      : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            <input
              className={`${fieldClass} uppercase font-mono font-medium`}
              value={vehicleNo}
              onChange={(e) => setVehicleNo(e.target.value)}
              placeholder="e.g. MH12AB1234 or Own Tempo"
            />
          </div>

          {/* Contact Mobile & LR / Tracking */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Driver / Contact Phone</label>
              <input
                className={fieldClass}
                inputMode="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="Driver phone number"
              />
            </div>
            <div>
              <label className={labelClass}>LR / Tracking / Docket No.</label>
              <input
                className={fieldClass}
                value={trackingNo}
                onChange={(e) => setTrackingNo(e.target.value)}
                placeholder="e.g. LR-987654 / Docket"
              />
            </div>
          </div>

          {/* ── Payment & Freight Section (Admin / Dept Admin / Super Admin Entry) ── */}
          <div className="rounded-xl border border-indigo-100 bg-indigo-50/30 p-3.5 space-y-3">
            <div className="flex items-center justify-between border-b border-indigo-100 pb-2">
              <span className="text-xs font-bold text-indigo-900 flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5 text-indigo-600" />
                Transport Payment &amp; Charges
              </span>
              {canSetAmount ? (
                <span className="text-[10px] font-bold bg-success/10 text-success-text px-2 py-0.5 rounded-full">
                  Admin Edit Enabled
                </span>
              ) : (
                <span className="text-[10px] font-semibold text-slate-500 flex items-center gap-1">
                  <ShieldAlert className="w-3 h-3 text-warning-text" /> Amount Managed by Admin
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-[11px] font-bold text-slate-700">Payment Status / Terms</label>
                <select
                  className={fieldClass}
                  value={payment}
                  onChange={(e) => setPayment(e.target.value)}
                >
                  <option value="to_pay">To Pay</option>
                  <option value="paid">Paid</option>
                  <option value="self">Self</option>
                  {!['to_pay', 'paid', 'self', ''].includes(payment) && (
                    <option value={payment}>{payment}</option>
                  )}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-[11px] font-bold text-slate-700">Payment Mode</label>
                <select
                  className={fieldClass}
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                >
                  {LOCAL_TRANSPORT_PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-[11px] font-bold text-slate-700">
                  Payment Amount (₹) {canSetAmount && <span className="text-indigo-600">*</span>}
                </label>
                {canSetAmount ? (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={`${fieldClass} font-bold text-slate-900`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="e.g. 500"
                  />
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      disabled
                      className={`${fieldClass} bg-slate-100/70 text-slate-400 cursor-not-allowed`}
                      value={initial?.amountEntered ? '₹ on file' : 'Pending Admin entry'}
                    />
                  </div>
                )}
              </div>
            </div>

            {canSetAmount && (
              <div className="pt-2 border-t border-indigo-100/80 space-y-1.5">
                <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-800 font-semibold select-none">
                  <input
                    type="checkbox"
                    checked={divideAcrossVehicle}
                    onChange={(e) => setDivideAcrossVehicle(e.target.checked)}
                    className="h-4 w-4 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>Divide transport cost equally if same vehicle carries multiple invoices</span>
                </label>
                <p className="text-[10px] text-indigo-700 font-medium">
                  When checked, the entered amount is divided equally among the orders/invoices that went on this vehicle number on the trip date below — another day is another trip.
                </p>
                {divideAcrossVehicle && (
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-800">
                    <span>Trip date</span>
                    <input
                      type="date"
                      value={shareDate}
                      onChange={(e) => setShareDate(e.target.value)}
                      className="rounded-lg border border-indigo-200 bg-white px-2 py-1 text-xs focus:border-indigo-500 focus:outline-none"
                    />
                    {!shareDate && <span className="text-[10px] font-normal text-slate-500">(blank = this order&apos;s dispatch date)</span>}
                  </label>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className={labelClass}>Note / Remarks (Optional)</label>
            <input
              className={fieldClass}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Plant Gate 2 dispatch, call before unloading"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="rounded-xl bg-indigo-600 hover:bg-indigo-500 px-5 py-2 text-xs font-bold text-white shadow transition flex items-center gap-1.5 cursor-pointer"
            >
              <Zap className="w-3.5 h-3.5" />
              <span>{busy ? 'Saving…' : 'Save Transport Details'}</span>
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}
